<template>
    <ConfirmDialog group="delete-form"></ConfirmDialog>
    <ReservationFormDrawer v-if="form" v-bind="props"></ReservationFormDrawer>
    <div v-if="forms" class="flex flex-col h-full md:gap-4">
        <div class="md:rounded-3xl  dark:border-neutral-700 grow pb-12">
            <div v-if="filteredGroupedForms" v-for="(forms, typeName) in filteredGroupedForms" :key="typeName" class="mb-8">
                <h3 class="text-lg font-bold mb-4 mt-10 first:mt-0">{{ $t('forms.type_' + typeName + '_heading') }}</h3>
                <DataView :value="forms" layout="grid">
                    <template #grid="{ items }">
                        <TransitionGroup
                            tag="div"
                            name="card"
                            class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
                            <DashboardCard v-for="item in items" :key="item.id">
                                <template #header>
                                    <div class="flex items-center justify-between gap-4 w-full">
                                        <div class="flex items-center gap-3 overflow-hidden grow">
                                            <span
                                                v-tooltip.bottom="getFormType(item.form_type.name)?.label"
                                                class="text-white text-lg w-8 h-8 rounded-full flex justify-center items-center"
                                                :class="[`bg-${item.theme?.color ?? 'blue'}-500`, getFormType(item.form_type.name)?.icon]">
                                            </span>
                                            <h4 class="text-lg font-bold truncate text-gray-900 dark:text-white">
                                                {{ item.name }}
                                            </h4>
                                        </div>
                                        <div class="flex items-center gap-1 calendar-wrapper">
                                            <p
                                                v-for="calendar in item.calendar_names"
                                                :key="calendar.name"
                                                :style="`color: ${calendar.color || '#333'}`"
                                                class="w-6 h-6 text-xs flex justify-center items-center rounded-full text-white"
                                                v-tooltip="calendar.name"
                                            >
                                                <span :class="'!text-sm pi pi-' + item.provider.name"></span>
                                            </p>
                                        </div>
                                    </div>
                                </template>

                                <template #body>
                                    <div class="mt-4 text-sm text-neutral-600 dark:text-neutral-300 space-y-3 grow">
                                        <div class="flex flex-col !text-xs justify-between opacity-65">
                                            <span>{{ $t('forms.col_created') }}: {{ item.human_created_at }}</span>
                                            <span>{{ $t('forms.col_updated') }}: {{ item.human_updated_at }}</span>
                                        </div>
                                        <div v-if="item.rental_items.length || item.reservation_services?.length" class="mb-4 bg-neutral-50 dark:bg-black dark:border-neutral-700 border rounded-lg relative p-2 grow ">
                                            <div v-if="item.rental_items.length">
                                                <div class="flex flex-wrap items-center gap-2">
                                                    <p class="font-bold text-xs absolute -top-3 right-4 bg-neutral-200 dark:bg-neutral-800 dark:border-neutral-700 border p-0.5 rounded-xl px-4">{{ $t('forms.col_objects') }}</p>
                                                    <div
                                                        v-for="item in item.rental_items"
                                                        :key="item.id"
                                                        class="bg-neutral-c200 border dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 dark:border-neutral-700 text-xs font-medium px-3 py-1 rounded flex items-center gap-2"
                                                    >
                                                        <i :class="`${item.type} text-sm`"></i> {{ item.name }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="item.reservation_services?.length">
                                                <div class="flex flex-wrap gap-2 grow">
                                                    <p class="font-bold text-xs absolute -top-3 right-4 bg-neutral-200 dark:bg-neutral-800 dark:border-neutral-700 border p-0.5 rounded-xl px-4">{{ $t('forms.col_services') }}</p>
                                                    <div
                                                        v-for="item in item.reservation_services"
                                                        :key="item.id"
                                                        class="bg-neutral-c200 border dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 dark:border-neutral-700 text-xs font-medium px-3 py-1 rounded flex items-center gap-2"
                                                    >
                                                        {{ item.name }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <template #main-actions>
                                    <div class="flex items-center gap-2">
                                        <share-component
                                            :slug="item.slug"
                                            :name="item.name"
                                            :url="item.url"
                                            :share-links="item.share_links"
                                        />

                                        <Button
                                            icon="pi pi-pencil"
                                            rounded
                                            text
                                            severity="secondary"
                                            v-tooltip.bottom="$t('forms.tooltip_edit')"
                                            class="hover:bg-gray-100 dark:hover:bg-gray-700"
                                            @click="router.visit('/app/forms/' + item.slug, { preserveState: false, preserveScroll: true })"
                                        />

                                        <Button
                                            icon="pi pi-trash"
                                            rounded
                                            text
                                            severity="danger"
                                            v-tooltip.bottom="$t('forms.tooltip_delete')"
                                            class="hover:bg-red-100 dark:hover:bg-red-900"
                                            @click="confirmDelete($event, item.slug)"
                                        />
                                    </div>
                                </template>

                                <template #secondary-actions>
                                    <Button
                                        as="a"
                                        :href="item.url"
                                        target="_blank"
                                        rel="noopener"
                                        v-tooltip.bottom="$t('forms.tooltip_open')"
                                        outlined
                                        rounded
                                        severity="secondary"
                                        :label="$t('common.open')"
                                        class="hover:bg-blue-100 dark:hover:bg-blue-900"
                                    />
                                </template>
                            </DashboardCard>
                        </TransitionGroup>
                    </template>
                </DataView>
            </div>
            <div v-else class="w-full p-16 h-full min-h-full flex flex-col gap-6 justify-center items-center text-center">
                <img src="../../../../../images/glass/Paper Plus.png" alt="No clients illustration" class="w-48 h-48 mb-6 mx-auto" />
                <h2 class="text-2xl font-semibold text-gray-700 dark:text-gray-300">{{ $t('forms.empty_title') }}</h2>
                <p class="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    {{ $t('forms.empty_description') }}
                </p>

                <Link href="/app/forms/wizard">
                    <Button
                        :label="$t('forms.btn_create')"
                        icon="pi pi-plus"
                        class="mt-4"
                        severity="primary"
                        rounded
                    />
                </Link>
            </div>

        </div>
    </div>
</template>
<script setup>
import {nextTick, onMounted, ref, computed} from 'vue'
import {router} from "@inertiajs/vue3";
import Button from 'primevue/button';
import {useConfirm} from "primevue/useconfirm";
import ConfirmDialog from "primevue/confirmdialog";
import ShareComponent from "../../components/ui-components/ShareComponent.vue";
import DataView from "primevue/dataview";
import ReservationFormDrawer from "../../components/view-components/ReservationFormDrawer.vue";
import {getFormType} from "../../../../helpers.js";
import DashboardCard from "../../components/ui-components/dashboard/DashboardCard.vue";
import {Link} from "@inertiajs/vue3";
import {trans} from "laravel-vue-i18n";

const confirm = useConfirm()

const props = defineProps({
    forms: Array,
    isExpanded: Boolean,
    form: Object,
    shown_driver: Number,
    valid_days: Object,
    title: String,
    templates: Array,
    services: Array,
    theme: Object,
    settings: Object,
    formBuilder: Array,
    rental_items: Array,
    locations: Array,
    workers: Array,
    expandSidebar: Boolean,
})

const isDOMmounted = ref(false);
const confirmDelete = (event, slug) => {
    confirm.require({
        group: 'delete-form',
        target: event.currentTarget,
        message: trans('forms.confirm_delete_message'),
        header: trans('forms.confirm_delete_title'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: trans('common.cancel'),
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: trans('forms.tooltip_delete'),
            severity: 'danger'
        },
        accept: () => {
            router.delete(`/app/forms/${slug}`)
        },
        reject: () => {

        }
    })
}


const selectedType = ref('');
const selectedProvider = ref('');
const searchQuery = ref('');

const filteredGroupedForms = computed(() => {
    if (!props.forms) return false;

    const result = {};

    const typeFilter = selectedType.value;
    const providerFilter = selectedProvider.value;
    const search = searchQuery.value?.toLowerCase() ?? '';

    for (const form of props.forms) {
        const type = form.form_type.name;

        const matchesType = !typeFilter || form.form_type.name === typeFilter;
        const matchesProvider = !providerFilter || form.provider.name === providerFilter;
        const matchesSearch = !search || form.name.toLowerCase().includes(search);

        if (!matchesType || !matchesProvider || !matchesSearch) continue;

        if (!result[type]) {
            result[type] = [];
        }

        result[type].push(form);
    }

    return Object.keys(result).length ? result : false;
});


onMounted(async () => {
    await nextTick();
    isDOMmounted.value = true;
})
</script>
<style scoped lang="scss">
.custom-avatar {
    padding: 4px 6px;
    width: 24px;
    height: 24px;
    color: var(--surface-border);
    font-size: .75rem;
    border: 1px solid var(--surface-border);
    border-radius: 6px;
}

.p-datatable {
    font-size: .875rem;
}

.status-col {
    display: flex;
    align-items: center;
    gap: 6px;

    .status-show {
        width: 8px;
        height: 8px;
        border-radius: 99px;
        display: block;
        background: black;

        &.online {
            background: rgb(106, 204, 13);

        }

        &.offline {
            background: rgb(204, 13, 13);

        }
    }
}

.card-enter-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.card-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
    position: absolute;
    pointer-events: none;
}

.card-enter-from {
    opacity: 0;
    transform: translateY(12px) scale(0.97);
}

.card-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
}

.card-move {
    transition: transform 0.3s ease;
}

.fade-expand-enter-active, .fade-expand-leave-active {
    transition: all 0.2s ease;
}

.fade-expand-enter-from, .fade-expand-leave-to {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
}
</style>
